'use strict'
import chalk from 'chalk'
import _ from 'lodash'
import type { CryptoKey, JWTPayload } from 'jose'
import { ed25519, x25519 } from '@noble/curves/ed25519'
import { bytesABase64Url, base64UrlABytes } from './p2p/emparejamiento/cripto'
import type { Bytes } from './p2p/emparejamiento/identidad'

import { ErrorAumentado } from './errorAumentado'
import cripto from './cripto'
import { HashU } from '@comun/hashU'

export type TipoLlavero = 'rsa' | 'ed25519'

export interface ParDeLlaves {
    publica: CryptoKey
    privada?: CryptoKey
}

export interface ParDeLlavesExportadas {
    publica: string
    privada?: string
}

export interface Ed25519Keys {
  firmaPublica: Bytes
  firmaPrivada?: Bytes
  ecdhPublica: Bytes
  ecdhPrivada?: Bytes
}

class Llavero {
  tipoLlavero: TipoLlavero
  llaveroID: string
  firma: ParDeLlaves
  encriptacion: ParDeLlaves
  ed25519?: Ed25519Keys

  constructor({
    tipoLlavero = 'rsa',
    llaveroID,
    firma,
    encriptacion,
    ed25519
  }: {
        tipoLlavero?: TipoLlavero
        llaveroID: string
        firma: ParDeLlaves
        encriptacion: ParDeLlaves
        ed25519?: Ed25519Keys
    }) {
    this.tipoLlavero = tipoLlavero
    this.llaveroID = llaveroID
    this.firma = firma
    this.encriptacion = encriptacion
    this.ed25519 = ed25519
  }

  static async reinstanciar({ firma: firmaExportada, encriptacion: encriptacionExportada, tipoLlavero }: {
        firma: ParDeLlavesExportadas
        encriptacion: ParDeLlavesExportadas
        tipoLlavero?: TipoLlavero
    }) {
    const llaveroID = await HashU(firmaExportada.publica + encriptacionExportada.publica)

    // Detección automática: claves Ed25519 son base64url cortas (~43 chars), RSA son PEM largas
    const esEd25519 = tipoLlavero === 'ed25519' || (
      !tipoLlavero &&
      firmaExportada.publica.length < 100 &&
      !firmaExportada.publica.includes('BEGIN')
    )

    if (esEd25519) {
      const firma = {
        publica: firmaExportada.publica as unknown as CryptoKey,
        privada: firmaExportada.privada as unknown as CryptoKey
      }
      const encriptacion = {
        publica: encriptacionExportada.publica as unknown as CryptoKey,
        privada: encriptacionExportada.privada as unknown as CryptoKey
      }
      return new Llavero({
        tipoLlavero: 'ed25519',
        llaveroID,
        firma,
        encriptacion,
        ed25519: {
          firmaPublica: base64UrlABytes(firmaExportada.publica),
          firmaPrivada: firmaExportada.privada ? base64UrlABytes(firmaExportada.privada) : undefined,
          ecdhPublica: base64UrlABytes(encriptacionExportada.publica),
          ecdhPrivada: encriptacionExportada.privada ? base64UrlABytes(encriptacionExportada.privada) : undefined
        }
      })
    }

    const firma = {
      publica: await cripto.importar.firma.publica(firmaExportada.publica),
      privada: firmaExportada.privada ? await cripto.importar.firma.privada(firmaExportada.privada) : undefined
    }

    const encriptacion = {
      publica: await cripto.importar.encriptacion.publica(encriptacionExportada.publica),
      privada: encriptacionExportada.privada ? await cripto.importar.encriptacion.privada(encriptacionExportada.privada) : undefined
    }

    const llavero = new Llavero({ tipoLlavero: 'rsa', llaveroID, firma, encriptacion })
    return llavero
  }

  static async generar(tipo: TipoLlavero = 'ed25519') {
    const fx = `generar`
    try {
      if (tipo === 'ed25519') {
        const firmaPrivada = ed25519.utils.randomSecretKey()
        const firmaPublica = ed25519.getPublicKey(firmaPrivada)
        const ecdhPrivada = x25519.utils.randomSecretKey()
        const ecdhPublica = x25519.getPublicKey(ecdhPrivada)

        const firmaPublicaB64u = bytesABase64Url(firmaPublica)
        const ecdhPublicaB64u = bytesABase64Url(ecdhPublica)

        const llaveroID = await HashU(firmaPublicaB64u + ecdhPublicaB64u)

        return new Llavero({
          tipoLlavero: 'ed25519',
          llaveroID,
          firma: { publica: firmaPublicaB64u as unknown as CryptoKey },
          encriptacion: { publica: ecdhPublicaB64u as unknown as CryptoKey },
          ed25519: {
            firmaPublica: firmaPublica,
            firmaPrivada: firmaPrivada,
            ecdhPublica: ecdhPublica,
            ecdhPrivada: ecdhPrivada
          }
        })
      }

      // RSA legacy
      const firma = await cripto.crearKeysFirmas()
      const encriptacion = await cripto.crearKeysEncriptacion()

      console.log(fx, { firma, encriptacion })

      const [
        firmaPublica,
        encriptacionPublica
      ] = await Promise.all([
        cripto.exportar.firma.publica(firma.publica),
        cripto.exportar.encriptacion.publica(encriptacion.publica)
      ])

      const llaveroID = await HashU(firmaPublica + encriptacionPublica)

      return new Llavero({ tipoLlavero: 'rsa', llaveroID, firma, encriptacion })
    }
    catch (e) {
      console.error(fx, e)
      throw e
    }
  }

  // Operaciones

  async encriptar(porEncriptar: JWTPayload) {
    const fx = `Llavero.encriptar`
    try {
      if (!this.encriptacion.publica)
        throw new ErrorAumentado(fx, { error: 'No existe llave publica de encriptación' })

      const llave = this.encriptacion.publica
      return await cripto.encriptar(llave, porEncriptar)
    }
    catch (e) {
      if (e instanceof ErrorAumentado)
        throw e.trazar(fx)
      console.error(chalk.bgRed(fx), e)
      throw new ErrorAumentado(fx, { error: e })
    }
  }

  async desencriptar(encriptado: string) {
    const fx = `Llavero.desencriptar`
    try {
      if (!this.encriptacion.privada)
        throw new ErrorAumentado(fx, { error: 'No existe llave privada de encriptación' })

      const llave = this.encriptacion.privada
      return await cripto.desencriptar(llave, encriptado)
    }
    catch (e) {
      if (e instanceof ErrorAumentado)
        throw e.trazar(fx)
      console.error(chalk.bgRed(fx), e)
      throw new ErrorAumentado(fx, { error: e })
    }
  }

  async firmar(porFirmar: JWTPayload): Promise<string> {
    const fx = `Llavero.firmar`
    try {
      if (this.tipoLlavero === 'ed25519') {
        if (!this.ed25519?.firmaPrivada)
          throw new ErrorAumentado(fx, { error: 'No existe llave privada ed25519' })
        const payload = new TextEncoder().encode(JSON.stringify(porFirmar))
        const firma = ed25519.sign(payload, this.ed25519.firmaPrivada)
        return bytesABase64Url(payload) + '.' + bytesABase64Url(firma)
      }

      if (!this.firma.privada)
        throw new ErrorAumentado(fx, { error: 'No existe llave privada de encriptación' })

      const llave = this.firma.privada
      return await cripto.firmar(llave, porFirmar, this.llaveroID)
    }
    catch (e) {
      if (e instanceof ErrorAumentado)
        throw e.trazar(fx)
      console.error(chalk.bgRed(fx), e)
      throw new ErrorAumentado(fx, { error: e })
    }
  }

  serializarEd25519(): { idCliente: string; firmaPublica: string; firmaPrivada: string; ecdhPublica: string; ecdhPrivada: string } {
    if (this.tipoLlavero !== 'ed25519') throw new ErrorAumentado('serializarEd25519', { error: 'No es llavero ed25519' })
    if (!this.ed25519) throw new ErrorAumentado('serializarEd25519', { error: 'Datos ed25519 faltantes' })
    return {
      idCliente: this.llaveroID,
      firmaPublica: bytesABase64Url(this.ed25519.firmaPublica),
      firmaPrivada: bytesABase64Url(this.ed25519.firmaPrivada!),
      ecdhPublica: bytesABase64Url(this.ed25519.ecdhPublica),
      ecdhPrivada: bytesABase64Url(this.ed25519.ecdhPrivada!)
    }
  }

  async serializar(): Promise<{ tipoLlavero: TipoLlavero; llaveroID: string; firmaPublica: string; firmaPrivada?: string; encriptacionPublica: string; encriptacionPrivada?: string }> {
    if (this.tipoLlavero === 'ed25519') {
      if (!this.ed25519) throw new ErrorAumentado('serializar', { error: 'Datos ed25519 faltantes' })
      return {
        tipoLlavero: 'ed25519',
        llaveroID: this.llaveroID,
        firmaPublica: bytesABase64Url(this.ed25519.firmaPublica),
        firmaPrivada: this.ed25519.firmaPrivada ? bytesABase64Url(this.ed25519.firmaPrivada) : undefined,
        encriptacionPublica: bytesABase64Url(this.ed25519.ecdhPublica),
        encriptacionPrivada: this.ed25519.ecdhPrivada ? bytesABase64Url(this.ed25519.ecdhPrivada) : undefined
      }
    }

    const [firmaPublica, firmaPrivada, encriptacionPublica, encriptacionPrivada] = await Promise.all([
      cripto.exportar.firma.publica(this.firma.publica),
      this.firma.privada ? cripto.exportar.firma.privada(this.firma.privada) : Promise.resolve(undefined as string | undefined),
      cripto.exportar.encriptacion.publica(this.encriptacion.publica),
      this.encriptacion.privada ? cripto.exportar.encriptacion.privada(this.encriptacion.privada) : Promise.resolve(undefined as string | undefined)
    ])

    return {
      tipoLlavero: 'rsa',
      llaveroID: this.llaveroID,
      firmaPublica,
      firmaPrivada,
      encriptacionPublica,
      encriptacionPrivada
    }
  }

  async exportarLlavesPublicas(): Promise<{ firma: string; encriptacion: string }> {
    if (this.tipoLlavero === 'ed25519') {
      if (!this.ed25519) throw new ErrorAumentado('exportarLlavesPublicas', { error: 'Datos ed25519 faltantes' })
      return {
        firma: bytesABase64Url(this.ed25519.firmaPublica),
        encriptacion: bytesABase64Url(this.ed25519.ecdhPublica)
      }
    }

    const [firma, encriptacion] = await Promise.all([
      cripto.exportar.firma.publica(this.firma.publica),
      cripto.exportar.encriptacion.publica(this.encriptacion.publica)
    ])

    return { firma, encriptacion }
  }

  static async deserializar(data: { tipoLlavero: TipoLlavero; llaveroID: string; firmaPublica: string; firmaPrivada?: string; encriptacionPublica: string; encriptacionPrivada?: string }): Promise<Llavero> {
    return Llavero.reinstanciar({
      firma: { publica: data.firmaPublica, privada: data.firmaPrivada },
      encriptacion: { publica: data.encriptacionPublica, privada: data.encriptacionPrivada },
      tipoLlavero: data.tipoLlavero
    })
  }

  async verificar(porVerificar: string, { permitirExpirado = false }: { permitirExpirado?: boolean } = {}): Promise<JWTPayload> {
    const fx = `Llavero.verificar`
    try {
      if (this.tipoLlavero === 'ed25519') {
        if (!this.ed25519?.firmaPublica)
          throw new ErrorAumentado(fx, { error: 'No existe llave publica ed25519' })
        const partes = porVerificar.split('.')
        if (partes.length !== 2) throw new ErrorAumentado(fx, { error: 'Token ed25519 malformado' })
        const payload = base64UrlABytes(partes[0]!)
        const firma = base64UrlABytes(partes[1]!)
        const valido = ed25519.verify(firma, payload, this.ed25519.firmaPublica)
        if (!valido) throw new ErrorAumentado(fx, { error: 'Firma ed25519 invalida' })
        return JSON.parse(new TextDecoder().decode(payload))
      }

      if (!this.firma.publica)
        throw new ErrorAumentado(fx, { error: 'No existe llave publica de encriptación' })

      const llave = this.firma.publica
      return await cripto.verificar(llave, porVerificar, { permitirExpirado })
    }
    catch (e) {
      if (e instanceof ErrorAumentado)
        throw e.trazar(fx)
      console.error(chalk.bgRed(fx), e)
      throw new ErrorAumentado(fx, { error: e })
    }
  }
}

export default Llavero
